import { FunctionComponent } from "preact";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import { useDigitalWallets, useSdk, useSession } from "./session-provider";
import { XenditPaymentChannel } from "../public-data-types";
import { assert } from "../utils";
import { DigitalWalletOptions } from "../public-options-types";
import { NetworkError } from "../networking";
import { getTelemetry, SessionTelemetryScope } from "../telemetry";
import { TelemetryEvents } from "../telemetry-events";

export const APPLE_PAY_VERSION = 14;

type ApplePayErrorCode =
  | "MERCHANT_VALIDATION_FAILED"
  | "NETWORK_ERROR"
  | "UNKNOWN_ERROR";

type Props = {
  options?: DigitalWalletOptions<"APPLE_PAY">;
  onReady: () => void;
};

function checkApplePayAvailability(): boolean {
  try {
    if (!window.ApplePaySession) return false;
    if (!ApplePaySession.supportsVersion(APPLE_PAY_VERSION)) return false;
    if (!ApplePaySession.canMakePayments()) return false;
    return true;
  } catch {
    return false;
  }
}

export const DigitalWalletApplepay: FunctionComponent<Props> = (props) => {
  const { onReady, options } = props;

  const sdk = useSdk();
  const t = sdk.t;

  const session = useSession();
  const digitalWallets = useDigitalWallets();
  const digitalWalletsApplePay = digitalWallets?.apple_pay;
  assert(digitalWalletsApplePay);

  const didCallReady = useRef(false);

  const buttonConfigWithDefaults = useMemo(
    () => ({
      buttonStyle: "black" as const,
      buttonType: "plain" as const,
      ...options,
    }),
    [options],
  );

  const cardsChannel: XenditPaymentChannel | undefined = useMemo(
    () =>
      sdk.getActiveChannels().find((ch) => {
        if (Array.isArray(ch.channelCode)) {
          return ch.channelCode.includes("CARDS");
        }
        return ch.channelCode === "CARDS";
      }),
    [sdk],
  );

  const telemetryScope = useRef<SessionTelemetryScope | null>(null);

  const telemetryForDigitalWalletClose = useCallback(
    (errorCode?: string) => {
      // telemetry for applepay error
      if (telemetryScope.current) {
        getTelemetry(sdk).append(
          TelemetryEvents.DigitalWalletClose(
            errorCode === undefined,
            errorCode,
          ),
        );
        // clear telemetry scope
        getTelemetry(sdk).popScope(telemetryScope.current);
        telemetryScope.current = null;
      }
    },
    [sdk],
  );

  const failSubmission = useCallback(
    (errorCode: ApplePayErrorCode) => {
      if (!cardsChannel) return;

      // Mirrors localeKeyForGooglePayError in digital-wallet-googlepay.tsx.
      function localeKeyForApplePayError<
        T extends ApplePayErrorCode,
        U extends "title" | "message",
      >(code: T, suffix: U) {
        return `apple_pay_errors.${code.toLowerCase() as Lowercase<T>}.${suffix}` as const;
      }

      const submissionError = {
        code: `APPLE_PAY_${errorCode}` as const,
        text: [
          t(
            localeKeyForApplePayError(errorCode, "title"),
            t("apple_pay_errors.unknown_error.title"),
          ),
          t(
            localeKeyForApplePayError(errorCode, "message"),
            t("apple_pay_errors.unknown_error.message"),
          ),
        ],
      };

      // telemetry for error/cancel
      telemetryForDigitalWalletClose(errorCode);

      // force error
      sdk.submitDigitalWallet(
        "APPLE_PAY",
        cardsChannel,
        {},
        false,
        submissionError,
      );
    },
    [cardsChannel, sdk, t, telemetryForDigitalWalletClose],
  );

  const onClick = useCallback(() => {
    if (!cardsChannel) return;

    let applePaySession: ApplePaySession;
    try {
      applePaySession = new ApplePaySession(
        APPLE_PAY_VERSION,
        digitalWalletsApplePay.apple_pay_payment_request,
      );
    } catch (err) {
      console.error("XenditComponents: Unable to start Apple Pay", err);
      failSubmission("UNKNOWN_ERROR");
      return;
    }

    applePaySession.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await sdk.validateApplePayMerchant(
          event.validationURL,
        );

        applePaySession.completeMerchantValidation(merchantSession);
      } catch (err) {
        applePaySession.abort();
        failSubmission(
          err instanceof NetworkError
            ? "MERCHANT_VALIDATION_FAILED"
            : "NETWORK_ERROR",
        );

        if (
          (sdk.isMock() || sdk.isDevelopmentEnv()) &&
          err instanceof NetworkError
        ) {
          alert(
            "Apple Pay merchant validation failed. In test mode, Apple Pay only works on domains that have been onboarded with Xendit, and on devices signed into an Apple sandbox account, and in countries supported by the Apple Pay sandbox.",
          );
        }
      }
    };

    applePaySession.onpaymentauthorized = (event) => {
      try {
        // telemetry for applepay close
        // (needs to be before submit or the event order is weird, even though the dialog is not actually closed until after the payment finishes)
        telemetryForDigitalWalletClose();

        // do submit
        sdk.submitDigitalWallet(
          "APPLE_PAY",
          cardsChannel,
          {
            apple_pay: JSON.stringify({
              billingContact: event.payment.billingContact,
              shippingContact: event.payment.shippingContact,
              token: event.payment.token,
            }),
          },
          false, // never try to save for applepay
        );
      } catch (err) {
        console.error(
          "XenditComponents: Unable to submit the Apple Pay payment",
          err,
        );
        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
        return;
      }
      applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
    };

    applePaySession.oncancel = () => {
      telemetryForDigitalWalletClose("USER_CANCELLED");
      // User dismissed the sheet, not an error and nothing was submitted
    };

    // telemetry for begin digital wallet flow
    telemetryScope.current = getTelemetry(sdk).appendAndPushScope(
      TelemetryEvents.DigitalWalletBegin(true, "APPLE_PAY"),
    );

    // start the flow
    applePaySession.begin();
  }, [
    cardsChannel,
    digitalWalletsApplePay.apple_pay_payment_request,
    failSubmission,
    sdk,
    telemetryForDigitalWalletClose,
  ]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  // not use useLayoutEffect because this check is synchronous, and firing onReady before the element is attached would lose the ready event.
  useEffect(() => {
    if (didCallReady.current) return;
    if (!cardsChannel) return;
    if (!checkApplePayAvailability()) return;

    didCallReady.current = true;
    onReady();
  }, [cardsChannel, onReady]);

  return (
    <apple-pay-button
      buttonstyle={buttonConfigWithDefaults.buttonStyle}
      type={buttonConfigWithDefaults.buttonType}
      locale={session.locale}
      className="xendit-apple-pay-button"
      onClick={onClick}
      onKeyDown={onKeyDown}
    />
  );
};

declare global {
  interface Window {
    ApplePaySession: typeof ApplePaySession;
  }
}

declare module "react/jsx-runtime" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": preact.DetailedHTMLProps<
        {
          buttonstyle?: string;
          type?: string;
          locale?: string;
          className?: string;
          onClick?: (e: MouseEvent) => void;
          onKeyDown?: (e: KeyboardEvent) => void;
        },
        HTMLButtonElement
      >;
    }
  }
}
